import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Clock, Globe, AlertCircle } from 'lucide-react';

// Common IANA timezones grouped by region
const TIMEZONE_OPTIONS = [
  { label: 'US & Canada', options: [
    { value: 'America/New_York', label: 'Eastern Time (New York)' },
    { value: 'America/Chicago', label: 'Central Time (Chicago)' },
    { value: 'America/Denver', label: 'Mountain Time (Denver)' },
    { value: 'America/Phoenix', label: 'Arizona (Phoenix) - No DST' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
    { value: 'America/Anchorage', label: 'Alaska (Anchorage)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii (Honolulu) - No DST' },
  ]},
  { label: 'India', options: [
    { value: 'Asia/Kolkata', label: 'India Standard Time (Kolkata)' },
  ]},
  { label: 'Europe', options: [
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
    { value: 'Europe/Moscow', label: 'Moscow (MSK)' },
  ]},
  { label: 'Asia & Pacific', options: [
    { value: 'Asia/Dubai', label: 'Dubai (GST)' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
    { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
  ]},
];

const DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const DEFAULT_SCHEDULE = {
  scheduling_enabled: false,
  timezone: 'America/Phoenix',
  manual_override: 'use_schedule',
  weekly_schedule: {
    monday:    { active: true,  start: '09:00', end: '17:00' },
    tuesday:   { active: true,  start: '09:00', end: '17:00' },
    wednesday: { active: true,  start: '09:00', end: '17:00' },
    thursday:  { active: true,  start: '09:00', end: '17:00' },
    friday:    { active: true,  start: '09:00', end: '17:00' },
    saturday:  { active: false, start: '09:00', end: '17:00' },
    sunday:    { active: false, start: '09:00', end: '17:00' },
  },
  offline_form_heading: "Our agents are currently offline",
  offline_form_description: "Leave us a message and an agent will get back to you, or continue chatting with our AI assistant!",
};

const ScheduleSettings = ({ config, setConfig }) => {
  const schedule = { ...DEFAULT_SCHEDULE, ...(config?.schedule_config || {}) };
  const weeklySchedule = { ...DEFAULT_SCHEDULE.weekly_schedule, ...(schedule.weekly_schedule || {}) };

  const updateSchedule = (patch) => {
    setConfig(prev => ({
      ...prev,
      schedule_config: {
        ...DEFAULT_SCHEDULE,
        ...(prev.schedule_config || {}),
        ...patch,
      }
    }));
  };

  const updateDay = (dayKey, dayPatch) => {
    const currentDay = weeklySchedule[dayKey] || DEFAULT_SCHEDULE.weekly_schedule[dayKey];
    updateSchedule({
      weekly_schedule: {
        ...weeklySchedule,
        [dayKey]: { ...currentDay, ...dayPatch },
      }
    });
  };

  return (
    <>
      {/* Enable/Disable Scheduling */}
      <Card className="border-2 border-gray-100 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-600" />
            Availability Schedule
          </CardTitle>
          <CardDescription>Control when human agents are available for live chat</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pt-6">

          {/* Enable Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-gray-600" />
              <div>
                <p className="font-medium">Enable Scheduling</p>
                <p className="text-sm text-gray-500">Set specific hours when agents are available for live chat</p>
              </div>
            </div>
            <div
              onClick={() => updateSchedule({ scheduling_enabled: !schedule.scheduling_enabled })}
              className={`w-12 h-6 rounded-full cursor-pointer transition-all duration-200 ${
                schedule.scheduling_enabled ? 'bg-[var(--g6-primary)]' : 'bg-gray-300'
              }`}
            >
              <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200 mt-0.5 ${
                schedule.scheduling_enabled ? 'translate-x-6 ml-0.5' : 'translate-x-0.5'
              }`} />
            </div>
          </div>

          {!schedule.scheduling_enabled && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <p className="text-sm text-green-700">Agents are always available (24/7) — AI chatbot is always online regardless</p>
            </div>
          )}

          {schedule.scheduling_enabled && (
            <div className="space-y-6">

              {/* Timezone Picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Globe className="w-4 h-4 inline mr-1" />
                  Timezone
                </label>
                <select
                  value={schedule.timezone || 'America/Phoenix'}
                  onChange={(e) => updateSchedule({ timezone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-[var(--g6-primary)] focus:border-transparent"
                >
                  {TIMEZONE_OPTIONS.map(group => (
                    <optgroup key={group.label} label={group.label}>
                      {group.options.map(tz => (
                        <option key={tz.value} value={tz.value}>{tz.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              {/* Manual Override */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  Manual Override
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'use_schedule', label: 'Use Schedule', desc: 'Follow the weekly schedule below' },
                    { value: 'force_online', label: 'Force Online', desc: 'Agents are always available regardless of schedule' },
                    { value: 'force_offline', label: 'Force Offline', desc: 'Agents unavailable — visitors can still chat with AI or leave a message' },
                  ].map(option => (
                    <label
                      key={option.value}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border-2 transition-all ${
                        schedule.manual_override === option.value
                          ? 'border-[var(--g6-primary)] bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="manual_override"
                        value={option.value}
                        checked={schedule.manual_override === option.value}
                        onChange={(e) => updateSchedule({ manual_override: e.target.value })}
                        className="w-4 h-4 text-[var(--g6-primary)]"
                      />
                      <div>
                        <p className="font-medium text-sm">{option.label}</p>
                        <p className="text-xs text-gray-500">{option.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Weekly Schedule Grid */}
              {schedule.manual_override === 'use_schedule' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Weekly Schedule</label>
                  <div className="space-y-2">
                    {/* Header */}
                    <div className="grid grid-cols-[1fr_100px_100px_60px] gap-2 px-3 text-xs font-semibold text-gray-500 uppercase">
                      <span>Day</span>
                      <span>Start</span>
                      <span>End</span>
                      <span className="text-center">Active</span>
                    </div>
                    {/* Day rows */}
                    {DAYS.map(day => {
                      const dayConfig = weeklySchedule[day.key] || { active: false, start: '09:00', end: '17:00' };
                      return (
                        <div
                          key={day.key}
                          className={`grid grid-cols-[1fr_100px_100px_60px] gap-2 items-center px-3 py-2 rounded-lg ${
                            dayConfig.active ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
                          }`}
                        >
                          <span className={`text-sm font-medium ${dayConfig.active ? 'text-gray-900' : 'text-gray-400'}`}>
                            {day.label}
                          </span>
                          <input
                            type="time"
                            value={dayConfig.start || '09:00'}
                            onChange={(e) => updateDay(day.key, { start: e.target.value })}
                            disabled={!dayConfig.active}
                            className="px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-40 disabled:bg-gray-100"
                          />
                          <input
                            type="time"
                            value={dayConfig.end || '17:00'}
                            onChange={(e) => updateDay(day.key, { end: e.target.value })}
                            disabled={!dayConfig.active}
                            className="px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-40 disabled:bg-gray-100"
                          />
                          <div className="flex justify-center">
                            <div
                              onClick={() => updateDay(day.key, { active: !dayConfig.active })}
                              className={`w-10 h-5 rounded-full cursor-pointer transition-all duration-200 ${
                                dayConfig.active ? 'bg-[var(--g6-primary)]' : 'bg-gray-300'
                              }`}
                            >
                              <div className={`w-4 h-4 bg-white rounded-full shadow-md transition-transform duration-200 mt-0.5 ${
                                dayConfig.active ? 'translate-x-5 ml-0.5' : 'translate-x-0.5'
                              }`} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Offline Form Customization */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Agent Offline Form Text</label>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Heading</label>
                    <Input
                      value={schedule.offline_form_heading || ''}
                      onChange={(e) => updateSchedule({ offline_form_heading: e.target.value })}
                      placeholder="Our agents are currently offline"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Description</label>
                    <Input
                      value={schedule.offline_form_description || ''}
                      onChange={(e) => updateSchedule({ offline_form_description: e.target.value })}
                      placeholder="Leave us a message and an agent will get back to you!"
                    />
                  </div>
                </div>
              </div>

            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default ScheduleSettings;
